import { Component, OnDestroy, OnInit, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Editor, Toolbar } from 'ngx-editor';
@Component({
  selector: 'app-text-editor',
  templateUrl: './text-editor.component.html',
  styleUrls: ['./text-editor.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextEditorComponent),
      multi: true
    }
  ]
})
export class TextEditorComponent implements ControlValueAccessor, OnInit, OnDestroy {
  private lastValidHtml: string = '';
  @Input() disabled: boolean = false;
  @Output() contentChange = new EventEmitter<string>();
  @Input() isDisplayText: boolean = true;
  // visibleTextEditor: boolean = true;
  currentLength: number = 0;
  maxLength: number = 400;
  private onChange: (value: any) => void = () => { };
  private onTouched: () => void = () => { };

  // Flag to avoid handling valueChanges when we programmatically update the control
  private isApplyingProgrammaticChange: boolean = false;
  // used to unregister DOM listeners added for hiding native title tooltips
  private _removeTitleListener: (() => void) | null = null;
  // MutationObserver for color picker popup
  private _colorPickerObserver: MutationObserver | null = null;
  // Track if color picker is open
  private _isColorPickerOpen: boolean = false;
  // Track if link popup is open
  // private _isLinkPopupOpen: boolean = false;
  // Debounce timer for color marking
  private _colorMarkTimer: any = null;
  // Interval for popup monitoring
  private _popupCheckInterval: any = null;
  // Reference to markActiveColor function
  private _markActiveColorRef: ((retryCount?: number) => void) | null = null;

  constructor() { }

  editor: Editor = new Editor();
  toolbar: Toolbar = [
    ['bold', 'italic', 'underline','link'],
    ['text_color'],
  ];

  colorPresets = ['#D0021B', '#F5A623', '#F8E71C', '#8B572A', '#7ED321', '#417505', '#9013FE', '#4A90E2', '#50E3C2', '#B8E986', '#000000', '#4A4A4A', '#FFFFFF', '#FF00FF', '#40FF00', '#FF0000'];

  form = new FormGroup({
    editorContent: new FormControl(
      { value: '', disabled: false }
    ),
  });

  get doc(): AbstractControl<any> | null {
    return this.form.get('editorContent');
  }

  ngOnInit(): void {

    this.editor = new Editor({
      inputRules: false,
    });

    this.form.get('editorContent')?.valueChanges.subscribe(value => {
      if (this.isApplyingProgrammaticChange) return; // ignore programmatic updates

      let htmlContent = value || '';

      // --- Remove all image tags (prevent image insertion) ---
      htmlContent = htmlContent.replace(/<img[^>]*>/gi, '');

      // --- Normalize common HTML line break patterns ---
      htmlContent = htmlContent
        .replace(/<div>/gi, '<p>')
        .replace(/<\/div>/gi, '</p>')
        .replace(/<br\s*\/?\>/gi, '</p><p>');

      // --- Count paragraphs and line breaks ---
      const paragraphMatches = htmlContent.match(/<p[^>]*>.*?<\/p>/gi) || [];
      const lineBreakCount = Math.max(0, paragraphMatches.length - 1);

      // --- Allow up to 3 line breaks (4 paragraphs max) ---
      if (lineBreakCount > 3) {
        // revert to last valid HTML (prevent extra new lines)
        this.isApplyingProgrammaticChange = true;
        this.form.get('editorContent')?.setValue(this.lastValidHtml, { emitEvent: false });
        this.isApplyingProgrammaticChange = false;
        return;
      }

      const plainText = this.htmlToPlainTextPreserveSpaces(htmlContent);
      this.currentLength = plainText.length;

      // Enable toolbar if there's content
      // if (plainText.trim().length > 0) {
      //   setTimeout(() => {
      //     this.enableToolbar();
      //   }, 0);
      // }

      // Clear formatting when editor becomes empty
      if (plainText.trim().length === 0) {
        this.clearAllFormatting();
      }

      if (plainText.length > this.maxLength) {
        // Truncate content to max length (handles paste operations)
        // Typing is already blocked by keydown handler, so this mainly handles paste
        const savedRange = this.saveSelection();
        const truncatedHtml = this.truncateHtmlPreserveFormatting(htmlContent, this.maxLength);
        this.isApplyingProgrammaticChange = true;
        this.form.get('editorContent')?.setValue(truncatedHtml, { emitEvent: false });
        this.isApplyingProgrammaticChange = false;
        this.lastValidHtml = truncatedHtml;
        this.currentLength = this.htmlToPlainTextPreserveSpaces(truncatedHtml).length;
        this.onChange(truncatedHtml);
        this.contentChange.emit(truncatedHtml);
        
        setTimeout(() => {
          this.restoreCursorPosition(savedRange, truncatedHtml);
        }, 0);
        return;
      } else {
        // For normal typing, don't preserve trailing spaces to avoid cursor jumping
        // Only preserve when necessary (like on paste or external updates)
        // This prevents cursor from jumping to end during normal typing
        this.lastValidHtml = htmlContent;
        this.onChange(htmlContent);
        this.contentChange.emit(htmlContent);
      }
    });

  }

  private htmlToPlainTextPreserveSpaces(html: string): string {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    // Remove image elements before counting text (images don't count toward character limit)
    const images = div.querySelectorAll('img');
    images.forEach(img => img.remove());
    let text = div.textContent || div.innerText || '';
    text = text.replace(/[\u200B-\u200D\uFEFF]/g, '');
    // This ensures &nbsp; entities are counted as regular spaces for character limit
    text = text.replace(/\u00A0/g, ' ');
    // convert CR to empty and keep other whitespace as-is
    text = text.replace(/\r/g, '');
    return text;
  }

  saveSelection() {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    return sel.getRangeAt(0).cloneRange();
  }

  restoreSelectionToEnd(savedRange: Range | null) {
    const editorElement = document.querySelector('.NgxEditor__Content');
    if (!savedRange || !editorElement) return;

    const sel = window.getSelection();
    if (!sel) return;

    // Move cursor to END of editor content
    const range = document.createRange();
    range.selectNodeContents(editorElement);
    range.collapse(false); // collapse = move to end

    sel.removeAllRanges();
    sel.addRange(range);
  }

// Restore cursor position, trying to maintain original position or move to end
  private restoreCursorPosition(savedRange: Range | null, newHtml: string) {
    const editorElement = document.querySelector('.NgxEditor__Content') as HTMLElement;
    if (!editorElement) return;

    const sel = window.getSelection();
    if (!sel) return;

    // Try to restore to the end of content (where user was typing)
    const range = document.createRange();
    range.selectNodeContents(editorElement);
    range.collapse(false); // collapse to end

    sel.removeAllRanges();
    sel.addRange(range);
  }

  ngAfterViewInit() {
    const editorElement = document.querySelector('.NgxEditor__Content') as HTMLElement;
    if (!editorElement) return;

    // 🔹 Enable hyperlink navigation (temporarily disable contenteditable on link click)
    editorElement.addEventListener('click', (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      // Check if clicked element or its parent is an anchor tag
      const linkElement = target.closest('a') as HTMLAnchorElement;
      
      if (linkElement && linkElement.href) {
        const href = linkElement.getAttribute('href');
        
        if (href && href.trim() !== '') {
          // Prevent default link behavior
          e.preventDefault();
          e.stopPropagation();
          
          // Temporarily disable contenteditable to allow navigation
          const wasContentEditable = editorElement.contentEditable;
          editorElement.contentEditable = 'false';
          
          // Open link in new tab
          window.open(href, '_blank', 'noopener,noreferrer');
          
          // Immediately restore contenteditable
          // Use requestAnimationFrame to ensure it happens after the click event
          requestAnimationFrame(() => {
            editorElement.contentEditable = wasContentEditable;
          });
        }
      }
    }, true); // Use capture phase to handle before ProseMirror's handlers

    // Check if editor has content - if so, enable toolbar
    // const hasContent = this.form.get('editorContent')?.value || '';
    // if (hasContent && hasContent.trim().length > 0) {
    //   // Enable toolbar if there's content
    //   setTimeout(() => {
    //     this.enableToolbar();
    //   }, 100);
    // } else {
    //   // Initially disable toolbar (will be enabled when editor gets focus)
    //   setTimeout(() => {
    //     this.disableToolbar();
    //   }, 100);
    // }

    // 🔹 Prevent image paste and block paste when at max length
    editorElement.addEventListener('paste', (e: ClipboardEvent) => {
      const savedRange = this.saveSelection();
      
      const items = e.clipboardData?.items;
      
      // Block image paste
      if (items) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.type.indexOf('image') !== -1) {
            e.preventDefault();
            e.stopPropagation();
            return; // Block image paste completely
          }
        }
      }
      
      // Allow paste - we'll truncate after paste if it exceeds max length

      // Clean background colors on paste (for non-image content)
      // Also strip any img tags from pasted HTML content
      // Truncate content if it exceeds max length
      setTimeout(() => {
        this.cleanBackgroundColor();
        // Double-check and remove any images that might have been pasted as HTML
        const editorElement = document.querySelector('.NgxEditor__Content') as HTMLElement;
        if (editorElement) {
          const images = editorElement.querySelectorAll('img');
          images.forEach(img => img.remove());
          
          // Check if content exceeds max length and truncate if needed
          let html = editorElement.innerHTML;
          const plainText = this.htmlToPlainTextPreserveSpaces(html);
          
          if (plainText.length > this.maxLength) {
            // Truncate HTML while preserving formatting
            html = this.truncateHtmlPreserveFormatting(html, this.maxLength);
            editorElement.innerHTML = html;
          }
          
          // Update form value
          this.isApplyingProgrammaticChange = true;
          this.form.get('editorContent')?.setValue(html, { emitEvent: true });
          this.isApplyingProgrammaticChange = false;
          this.lastValidHtml = html;
          this.currentLength = this.htmlToPlainTextPreserveSpaces(html).length;
        }
        this.restoreSelectionToEnd(savedRange);
        
        // If color picker is open, refresh the color marks after paste
        if (this._isColorPickerOpen && this._markActiveColorRef) {
          setTimeout(() => {
            this._markActiveColorRef!(0);
          }, 100);
        }
      }, 10);
    });

    // 🔹 Prevent drag and drop images
    editorElement.addEventListener('dragover', (e: DragEvent) => {
      // Check if dragging image files
      if (e.dataTransfer?.types.includes('Files')) {
        const files = e.dataTransfer.files;
        for (let i = 0; i < files.length; i++) {
          if (files[i].type.startsWith('image/')) {
            e.preventDefault();
            e.stopPropagation();
            return; // Block image drag over
          }
        }
      }
    });

    editorElement.addEventListener('drop', (e: DragEvent) => {
      // Block image drop
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          if (file.type.startsWith('image/')) {
            e.preventDefault();
            e.stopPropagation();
            return; // Block image drop completely
          }
        }
      }
      
      // Allow text drop - valueChanges will truncate if it exceeds max length
      if (e.dataTransfer?.types.includes('text/plain')) {
        setTimeout(() => {
          const editorElement = document.querySelector('.NgxEditor__Content') as HTMLElement;
          if (editorElement) {
            let html = editorElement.innerHTML;
            const plainText = this.htmlToPlainTextPreserveSpaces(html);
            
            if (plainText.length > this.maxLength) {
              // Truncate HTML while preserving formatting
              html = this.truncateHtmlPreserveFormatting(html, this.maxLength);
              editorElement.innerHTML = html;
              
              this.isApplyingProgrammaticChange = true;
              this.form.get('editorContent')?.setValue(html, { emitEvent: true });
              this.isApplyingProgrammaticChange = false;
              this.lastValidHtml = html;
              this.currentLength = this.htmlToPlainTextPreserveSpaces(html).length;
            }
          }
        }, 10);
      }
    });

    // 🔹 Block input when max length is reached
    // Use capture phase and stopImmediatePropagation to prevent platform/browser
    // handlers from causing page scroll (observed on macOS/Safari).
    editorElement.addEventListener('keydown', (e: KeyboardEvent) => {
      // Check current length
      const html = this.form.get('editorContent')?.value || '';
      const plainText = this.htmlToPlainTextPreserveSpaces(html);
      const currentLength = plainText.length;

      // Allow special keys (backspace, delete, arrow keys, etc.) even at max length
      const allowedKeys = [
        'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
        'Home', 'End', 'PageUp', 'PageDown', 'Tab', 'Escape'
      ];
      
      // Allow Ctrl/Cmd combinations (for shortcuts like Ctrl+A, Ctrl+C, etc.)
      const isModifierKey = e.ctrlKey || e.metaKey || e.altKey;
      
      // Block typing if at max length (unless it's a special key or modifier combination)
      // Also check if there's selected text that would be replaced
      const selection = window.getSelection();
      const hasSelection = selection && selection.rangeCount > 0 && !selection.isCollapsed;
      
      if (currentLength >= this.maxLength && !allowedKeys.includes(e.key) && !isModifierKey) {
        // Allow if there's selected text that would be replaced (user is replacing, not adding)
        if (!hasSelection) {
          // Check if it's a printable character or Enter
          if (e.key.length === 1 || e.key === 'Enter') {
            try { (e as any).stopImmediatePropagation(); } catch (_) { }
            e.preventDefault();
            try { e.stopPropagation(); } catch (_) { }
            try { (e as any).returnValue = false; } catch (_) { }
            return;
          }
        }
      }

      // Block Enter / Shift+Enter after 3 breaks
      if (e.key === 'Enter') {
        const breakCount = this.countLineBreaks(html);
        if (breakCount >= 3) {
          try { (e as any).stopImmediatePropagation(); } catch (_) { }
          e.preventDefault();
          try { e.stopPropagation(); } catch (_) { }
          try { (e as any).returnValue = false; } catch (_) { }

          // Some Mac browsers still scroll the page even after preventing the
          // event — capture current scroll position and restore it immediately
          // after the event loop to ensure the viewport doesn't jump.
          const scrollX = window.scrollX || window.pageXOffset || 0;
          const scrollY = window.scrollY || window.pageYOffset || 0;
          setTimeout(() => {
            try { window.scrollTo(scrollX, scrollY); } catch (_) { }
          }, 0);
        }
      }
    }, true);

    editorElement.addEventListener('keydown', (e: KeyboardEvent) => {
      // Prevent formatBlock, insertUnorderedList, insertOrderedList commands
      if ((e.key === 'Enter' || e.key.match(/[0-9*\-+]/)) && e.ctrlKey) {
        e.preventDefault();
      }
    }, true);

    // 🔹 Disable/enable toolbar based on editor focus
    editorElement.addEventListener('focus', () => {
      this.enableToolbar();
    });

    editorElement.addEventListener('blur', () => {
      // Don't disable toolbar if a popup (link or color picker) is open or if there's content
      // Disable toolbar when editor loses focus, but only if no popup is open and no content
      setTimeout(() => {
        // Double-check that editor is still not focused and no popup is open
        const editorElement = document.querySelector('.NgxEditor__Content') as HTMLElement;
        const activeElement = document.activeElement;
        
        // Check if there's content
        // const hasContent = this.form.get('editorContent')?.value || '';
        // const plainText = this.htmlToPlainTextPreserveSpaces(hasContent);
        // const hasTextContent = plainText.trim().length > 0;
        
        // // Only disable if editor is not focused AND no popup is open AND no content
        // if (!editorElement || 
        //     (activeElement !== editorElement && 
        //      !editorElement.contains(activeElement) && 
        //      !this._isLinkPopupOpen && 
        //      !this._isColorPickerOpen &&
        //      !hasTextContent)) {
        if (!editorElement || (activeElement !== editorElement && !editorElement.contains(activeElement))) {
          this.disableToolbar();
        }
      }, 100);
    });

    // 🔹 Keep editor focused when clicking toolbar buttons
    const setupToolbarClickHandlers = () => {
      const toolbar = document.querySelector('.NgxEditor__MenuBar');
      if (toolbar) {
        const buttons = toolbar.querySelectorAll('button');
        buttons.forEach(button => {
          // Add mousedown listener to focus editor before button action
          (button as HTMLElement).addEventListener('mousedown', (e) => {
            // Focus editor before button action
            const editorElement = document.querySelector('.NgxEditor__Content') as HTMLElement;
            if (editorElement) {
              editorElement.focus();
              this.enableToolbar();
            }
          }, true); // Use capture phase to run before ngx-editor's handlers
        });
      }
    };

    // Setup toolbar click handlers with retries
    // setTimeout(() => setupToolbarClickHandlers(), 100);
    // setTimeout(() => setupToolbarClickHandlers(), 500);

    // // 🔹 Setup link button handler to ensure editor is focused and selection is preserved
    // const setupLinkButton = () => {
    //   const linkButton = document.querySelector('button[title="Link"]') as HTMLButtonElement;
    //   if (linkButton && !linkButton.hasAttribute('data-link-setup')) {
    //     linkButton.setAttribute('data-link-setup', 'true');
        
    //     linkButton.addEventListener('mousedown', (e) => {
    //       const editorElement = document.querySelector('.NgxEditor__Content') as HTMLElement;
    //       if (editorElement) {
    //         // Save current selection
    //         const savedRange = this.saveSelection();
            
    //         // Enable toolbar first (this enables all buttons)
    //         this.enableToolbar();
            
    //         // Explicitly enable the link button to ensure it's not disabled
    //         this.enableToolbarButton(linkButton);
            
    //         // Set flag that link popup will open (will be confirmed by MutationObserver)
    //         // This prevents toolbar from being disabled when focus moves to popup
    //         setTimeout(() => {
    //           this._isLinkPopupOpen = true;
    //         }, 50);
            
    //         // Focus the editor
    //         editorElement.focus();
            
    //         // If there's no selection, move cursor to end
    //         if (!savedRange) {
    //           this.setCursorToEnd(editorElement);
    //         } else {
    //           // Restore selection
    //           try {
    //             const sel = window.getSelection();
    //             if (sel) {
    //               sel.removeAllRanges();
    //               sel.addRange(savedRange);
    //             }
    //           } catch (err) {
    //             // If restoration fails, move to end
    //             this.setCursorToEnd(editorElement);
    //           }
    //         }
    //       }
    //     }, true); // Use capture phase to run before ngx-editor's handlers
    //   }
    // };

    // // Initial setup - try multiple times in case button isn't ready yet
    // setupLinkButton();
    // setTimeout(() => setupLinkButton(), 100);
    // setTimeout(() => setupLinkButton(), 500);

    // Watch for color picker popup to appear and add title to remove button, and mark active color
    const addTitleToRemoveButton = () => {
      const addDeleteColorToltipButton = document.querySelector('.NgxEditor__MenuItem .NgxEditor__Popup button.NgxEditor__MenuItem--Button') as HTMLButtonElement;
      if (addDeleteColorToltipButton && !addDeleteColorToltipButton.getAttribute('title')) {
        addDeleteColorToltipButton.setAttribute('title', 'Remove color');
      }

      // Use querySelectorAll and iterate through the buttons
      const removeColorTooltipButtons = document.querySelectorAll('.NgxEditor__MenuItem .NgxEditor__Popup button.NgxEditor__Color') as NodeListOf<HTMLButtonElement>;
      removeColorTooltipButtons.forEach(button => {
        if (button && button.getAttribute('title')) {
          const titleValue = button.getAttribute('title');
          if (titleValue) {
            button.removeAttribute('title');
            button.setAttribute('colorCode', titleValue);
          }
        }
      });
    };

    // Function to mark active color in color picker (with retry mechanism)
    const markActiveColor = (retryCount: number = 0) => {
      const maxRetries = 5;
      
      // Clear any existing timer
      if (this._colorMarkTimer) {
        clearTimeout(this._colorMarkTimer);
        this._colorMarkTimer = null;
      }

      // Get all current text colors from editor state (can be multiple in selection)
      const currentColors = this.getCurrentTextColors();
      
      // Check if white is already in the detected colors
      const hasWhiteColor = currentColors.some(c => {
        const normalized = c.toLowerCase().replace('#', '').trim();
        return normalized === 'ffffff' || normalized === 'fff' || c.toLowerCase() === 'white';
      });
      
      // Check if selection contains text with default/white color (no explicit color applied)
      const hasDefaultWhiteInSelection = this.hasDefaultWhiteInSelection();
      
      // Build colors to match: include all detected colors
      const colorsToMatch: string[] = [];
      
      // Add all detected colors (including white if it was explicitly applied)
      currentColors.forEach(color => {
        if (!colorsToMatch.includes(color)) {
          colorsToMatch.push(color);
        }
      });
      
      // Add white if it exists in selection as default color (not explicitly applied)
      // This handles the case where some text has colors and some has default white
      if (hasDefaultWhiteInSelection && !hasWhiteColor) {
        colorsToMatch.push('#FFFFFF', '#ffffff', 'FFFFFF', 'ffffff', 'white', 'White');
      }
      
      // Check if popup exists and has color boxes
      const popup = document.querySelector('.NgxEditor__Popup');
      const colorBoxes = popup ? popup.querySelectorAll('.NgxEditor__Color') : null;
      
      // If popup doesn't exist or no color boxes, retry if we haven't exceeded max retries
      if (!popup || !colorBoxes || colorBoxes.length === 0) {
        if (retryCount < maxRetries) {
          this._colorMarkTimer = setTimeout(() => {
            markActiveColor(retryCount + 1);
          }, 50);
        }
        return;
      }

      // Use requestAnimationFrame for better timing
      requestAnimationFrame(() => {
        // Double-check popup still exists
        const currentPopup = document.querySelector('.NgxEditor__Popup');
        if (!currentPopup) {
          if (retryCount < maxRetries) {
            this._colorMarkTimer = setTimeout(() => {
              markActiveColor(retryCount + 1);
            }, 50);
          }
          return;
        }

        const boxes = currentPopup.querySelectorAll('.NgxEditor__Color') as NodeListOf<HTMLElement>;
        if (boxes.length === 0) {
          if (retryCount < maxRetries) {
            this._colorMarkTimer = setTimeout(() => {
              markActiveColor(retryCount + 1);
            }, 50);
          }
          return;
        }
        
        // Get color presets for matching
        const colorPresets = this.colorPresets.map(c => c.toLowerCase().replace('#', ''));
        
        boxes.forEach((box, index) => {
          // Remove any existing checkmarks
          const existingCheck = box.querySelector('.color-checkmark');
          if (existingCheck) {
            existingCheck.remove();
          }
          box.classList.remove('NgxEditor__Color--Active');

          // Get the color from the box - try multiple methods
          let boxHex = '';
          
          // Method 1: Try to get from colorCode attribute (we set this earlier)
          const colorCode = box.getAttribute('colorCode');
          if (colorCode) {
            boxHex = colorCode;
          }
          
          // Method 2: Try to get from backgroundColor
          if (!boxHex) {
            const boxColor = window.getComputedStyle(box).backgroundColor;
            if (boxColor && boxColor !== 'rgba(0, 0, 0, 0)' && boxColor !== 'transparent') {
              boxHex = this.rgbToHex(boxColor);
            }
          }
          
          // Method 3: Try to get from title attribute
          if (!boxHex) {
            const title = box.getAttribute('title');
            if (title && (title.startsWith('#') || title.match(/^[0-9a-fA-F]{6}$/))) {
              boxHex = title.startsWith('#') ? title : '#' + title;
            }
          }
          
          // Method 4: Use color preset by index if available
          if (!boxHex && index < this.colorPresets.length) {
            boxHex = this.colorPresets[index];
          }

          // If we still don't have a color and have colors to match, skip this box
          if (!boxHex && colorsToMatch.length > 0) {
            return;
          }

          // Check if this box color matches any of the current colors (or white if default)
          const matches = colorsToMatch.length > 0 && colorsToMatch.some(currentColor => {
            if (!currentColor || !boxHex) return false;
            
            // Handle named color "white"
            if (currentColor.toLowerCase() === 'white') {
              const whiteHex = '#FFFFFF';
              const normalizedBox = boxHex.toLowerCase().replace('#', '').trim();
              return normalizedBox === 'ffffff' || normalizedBox === 'fff';
            }
            
            // Normalize both colors for comparison
            const normalizedCurrent = currentColor.toLowerCase().replace('#', '').trim();
            const normalizedBox = boxHex.toLowerCase().replace('#', '').trim();
            
            // Direct match
            if (normalizedCurrent === normalizedBox) return true;
            
            // Handle 3-digit hex (e.g., #fff = #ffffff)
            if (normalizedCurrent.length === 3 && normalizedBox.length === 6) {
              const expanded = normalizedCurrent.split('').map(c => c + c).join('');
              if (expanded === normalizedBox) return true;
            }
            if (normalizedBox.length === 3 && normalizedCurrent.length === 6) {
              const expanded = normalizedBox.split('').map(c => c + c).join('');
              if (expanded === normalizedCurrent) return true;
            }
            
            // Also check against color presets - sometimes colors are stored differently
            // Convert both to RGB and compare (with tolerance for slight differences)
            try {
              const currentRgb = this.hexToRgb(currentColor);
              const boxRgb = this.hexToRgb(boxHex);
              if (currentRgb && boxRgb) {
                // Allow small tolerance for color matching (within 5 units)
                const tolerance = 5;
                return Math.abs(currentRgb.r - boxRgb.r) <= tolerance &&
                       Math.abs(currentRgb.g - boxRgb.g) <= tolerance &&
                       Math.abs(currentRgb.b - boxRgb.b) <= tolerance;
              }
            } catch (e) {
              // Ignore conversion errors
            }
            
            return false;
          });
          
          if (matches) {
            box.classList.add('NgxEditor__Color--Active');
            // Add checkmark icon
            const checkmark = document.createElement('span');
            checkmark.className = 'color-checkmark';
            checkmark.innerHTML = '✓';
            checkmark.style.cssText = `
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              color: white;
              font-size: 14px;
              font-weight: bold;
              z-index: 10;
              text-shadow: 0 0 2px rgba(0,0,0,0.8);
            `;
            box.appendChild(checkmark);
          }
        });
      });
    };

    // Store markActiveColor function reference for use in multiple places
    const markActiveColorRef = markActiveColor;
    
    // Store reference as class property for access from other methods
    this._markActiveColorRef = markActiveColorRef;

    // Listen for color picker icon click to add title to remove button and mark active color
    const setupColorPickerButton = () => {
    const colorPickerButton = document.querySelector('button[title="Text Color"]') as HTMLButtonElement;
    if (colorPickerButton) {
        // Add event listener without removing the existing one (ngx-editor's handler)
        // Use capture phase to ensure we run after ngx-editor's handler
        colorPickerButton.addEventListener('click', (e) => {
          // Check if editor is focused - if not, focus it and move cursor to end
          const editorElement = document.querySelector('.NgxEditor__Content') as HTMLElement;
          const activeElement = document.activeElement;
          // Check if editor or any of its children is focused
          const isEditorFocused = editorElement && (
            activeElement === editorElement || 
            editorElement.contains(activeElement)
          );
          
          // Set flag immediately - ngx-editor will open popup when it processes the click
          this._isColorPickerOpen = true;
          
          if (!isEditorFocused && editorElement) {
            // Focus the editor first
            editorElement.focus();
            // Move cursor to the end of the content immediately
            this.setCursorToEnd(editorElement);
            // Wait a bit for the editor state to update before marking active color
            // ngx-editor will open the popup, we just need to wait for state update
            requestAnimationFrame(() => {
              setTimeout(() => {
                // Use multiple timing strategies - wait a bit for ngx-editor to open popup
                requestAnimationFrame(() => {
                  setTimeout(() => {
                    addTitleToRemoveButton();
                    markActiveColorRef(0);
                  }, 100); // Longer delay to ensure editor state is updated
                });
              }, 50);
            });
          } else {
            // Editor is already focused, proceed normally
            // Don't prevent default or stop propagation - let ngx-editor handle it
            // Use multiple timing strategies - wait a bit for ngx-editor to open popup
            requestAnimationFrame(() => {
              setTimeout(() => {
                addTitleToRemoveButton();
                markActiveColorRef(0);
              }, 50); // Small delay to let ngx-editor open the popup first
            });
          }
        }, true); // Use capture phase
      }
    };

    // Initial setup - try multiple times in case button isn't ready yet
    setupColorPickerButton();
    
    // Retry setup if button wasn't found initially
    setTimeout(() => {
      setupColorPickerButton();
    }, 100);
    
    setTimeout(() => {
      setupColorPickerButton();
    }, 500);

    // Also listen for when color picker popup appears (using MutationObserver with better detection)
    this._colorPickerObserver = new MutationObserver((mutations) => {
      let popupAdded = false;
      // let isLinkPopup = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as HTMLElement;
              // Check if popup was added
              if (element.classList?.contains('NgxEditor__Popup') || 
                  element.querySelector?.('.NgxEditor__Popup')) {
                popupAdded = true;
                
                // Check if it's a link popup (has input fields for URL and Text)
                // const popup = element.classList?.contains('NgxEditor__Popup') 
                //   ? element 
                //   : element.querySelector?.('.NgxEditor__Popup') as HTMLElement;
                
                // if (popup) {
                //   // Check for link popup by looking for URL input and Insert button
                //   const urlInput = popup.querySelector('input[placeholder*="URL"], input[type="url"], input[type="text"]');
                //   const textInput = popup.querySelectorAll('input[type="text"]');
                //   const insertButton = popup.querySelector('button[type="submit"]') || 
                //                      Array.from(popup.querySelectorAll('button')).find(btn => {
                //                        const btnText = btn.textContent?.toLowerCase() || '';
                //                        return btnText.includes('insert') || btnText.includes('add') || btnText.includes('ok');
                //                      }) as HTMLButtonElement;
                  
                //   // If it has URL input and Insert button, it's likely a link popup
                //   if (urlInput && insertButton) {
                //     isLinkPopup = true;
                //     this._isLinkPopupOpen = true; // Track that link popup is open
                    
                //     // Handle link popup - ensure editor stays focused and toolbar is enabled
                //     const editorElement = document.querySelector('.NgxEditor__Content') as HTMLElement;
                //     if (editorElement) {
                //       // Enable toolbar and ensure editor is focused when popup opens
                //       this.enableToolbar();
                //       requestAnimationFrame(() => {
                //         setTimeout(() => {
                //           editorElement.focus();
                //           // Explicitly enable the link button
                //           const linkButton = document.querySelector('button[title="Link"]') as HTMLButtonElement;
                //           this.enableToolbarButton(linkButton);
                //         }, 50);
                //       });
                      
                //       // Handle Insert button click
                //       if (!insertButton.hasAttribute('data-link-handler-attached')) {
                //         insertButton.setAttribute('data-link-handler-attached', 'true');
                //         insertButton.addEventListener('click', (e) => {
                //           // Enable toolbar and ensure editor is focused before inserting link
                //           this.enableToolbar();
                //           const linkButton = document.querySelector('button[title="Link"]') as HTMLButtonElement;
                //           this.enableToolbarButton(linkButton);
                          
                //           // Clear link popup flag after a delay (popup will close)
                //           setTimeout(() => {
                //             this._isLinkPopupOpen = false;
                //           }, 200);
                          
                //           requestAnimationFrame(() => {
                //             setTimeout(() => {
                //               editorElement.focus();
                //               // Move cursor to end if no selection
                //               const selection = window.getSelection();
                //               if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
                //                 this.setCursorToEnd(editorElement);
                //               }
                //             }, 10);
                //           });
                //         }, true); // Use capture phase
                //       }
                      
                //       // Handle form submission
                //       const linkForm = popup.querySelector('form') as HTMLFormElement;
                //       if (linkForm && !linkForm.hasAttribute('data-link-handler-attached')) {
                //         linkForm.setAttribute('data-link-handler-attached', 'true');
                //         linkForm.addEventListener('submit', (e) => {
                //           // Enable toolbar and ensure editor is focused before submitting
                //           this.enableToolbar();
                //           const linkButton = document.querySelector('button[title="Link"]') as HTMLButtonElement;
                //           this.enableToolbarButton(linkButton);
                          
                //           // Clear link popup flag after a delay (popup will close)
                //           setTimeout(() => {
                //             this._isLinkPopupOpen = false;
                //           }, 200);
                          
                //           requestAnimationFrame(() => {
                //             setTimeout(() => {
                //               editorElement.focus();
                //             }, 10);
                //           });
                //         }, true);
                //       }
                //     }
                //   }
                // }
              }
            }
          });
        }
      });

      if (popupAdded) {
        this._isColorPickerOpen = true;
        // Use requestAnimationFrame for better timing
        requestAnimationFrame(() => {
          setTimeout(() => {
            addTitleToRemoveButton();
            markActiveColorRef(0);
          }, 0);
        });
      } else {
        // Check if popup was removed
        const popup = document.querySelector('.NgxEditor__Popup');
        if (!popup) {
          this._isColorPickerOpen = false;
        //   this._isLinkPopupOpen = false;
        // } else {
        //   // Check if link popup is still open
        //   const hasLinkPopup = popup.querySelector('input[placeholder*="URL"], input[type="url"]') ||
        //                       popup.querySelector('input[type="text"]')?.closest('form');
        //   if (!hasLinkPopup) {
        //     this._isLinkPopupOpen = false;
        //   }
        }
      }
    });

    // Observe the document body for popup appearance
    this._colorPickerObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false
    });

    // Monitor when popup is open and refresh colors periodically
    const startPopupMonitoring = () => {
      if (this._popupCheckInterval) return;
      
      this._popupCheckInterval = setInterval(() => {
        const popup = document.querySelector('.NgxEditor__Popup');
        if (popup && popup.querySelector('.NgxEditor__Color')) {
          this._isColorPickerOpen = true;
          // Refresh color marks periodically while popup is open
          markActiveColorRef(0);
        } else {
          this._isColorPickerOpen = false;
        }
      }, 300);
    };

    // Start monitoring
    startPopupMonitoring();

    // Store cleanup function
    this._removeTitleListener = () => {
      if (this._colorPickerObserver) {
        this._colorPickerObserver.disconnect();
        this._colorPickerObserver = null;
      }
    };
  }

  /**
   * Check if selection contains text with default/white color (no explicit color applied)
   */
  private hasDefaultWhiteInSelection(): boolean {
    try {
      const editorElement = document.querySelector('.NgxEditor__Content') as HTMLElement;
      if (!editorElement) return false;

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        // If no selection, check if editor is empty or cursor position
        const editorText = editorElement.textContent || '';
        return editorText.trim().length === 0;
      }

      const range = selection.getRangeAt(0);
      if (range.collapsed) {
        // Cursor position - check if there's text and no color
        const editorText = editorElement.textContent || '';
        return editorText.trim().length === 0;
      }

      // Check if selection contains text nodes without explicit color
      const walker = document.createTreeWalker(
        range.commonAncestorContainer,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: (node) => {
            try {
              if (range.intersectsNode(node)) {
                return NodeFilter.FILTER_ACCEPT;
              }
            } catch (e) {
              // Ignore errors
            }
            return NodeFilter.FILTER_REJECT;
          }
        }
      );

      let node: Node | null = walker.currentNode || range.commonAncestorContainer;
      let hasDefaultColorText = false;

      do {
        if (node && node.nodeType === Node.TEXT_NODE && node.textContent && node.textContent.trim().length > 0) {
          // Check parent elements for color
          let parent = node.parentElement;
          let hasExplicitColor = false;

          while (parent && parent !== editorElement) {
            // Check if parent has explicit color style
            const styleColor = parent.style.color;
            if (styleColor && styleColor.trim().length > 0) {
              hasExplicitColor = true;
              break;
            }

            // Check if parent is inside a span with color
            const coloredSpan = parent.closest('span[style*="color"]');
            if (coloredSpan) {
              hasExplicitColor = true;
              break;
            }

            parent = parent.parentElement;
          }

          // If no explicit color found, this text has default color
          if (!hasExplicitColor) {
            hasDefaultColorText = true;
            break;
          }
        }

        node = walker.nextNode();
      } while (node);

      return hasDefaultColorText;
    } catch (error) {
      console.error('Error checking default white in selection:', error);
      return false;
    }
  }

  /**
   * Get all current text colors from editor state (for selections with multiple colors)
   */
  private getCurrentTextColors(): string[] {
    const colors: string[] = [];
    const colorSet = new Set<string>();

    try {
      const state = this.editor.view.state;
      const sel = state.selection;
      const { from, to, empty } = sel;

      if (empty) {
        // Get marks at cursor position
        const marks = state.storedMarks || state.doc.resolve(from).marks();
        const colorMark = marks.find(m => m.type.name === 'text_color');
        if (colorMark && colorMark.attrs && (colorMark.attrs as any)['color']) {
          const color = (colorMark.attrs as any)['color'];
          if (color && !colorSet.has(color.toLowerCase())) {
            colorSet.add(color.toLowerCase());
            colors.push(color);
          }
        }
      } else {
        // For selection, scan through all characters to find all colors
        for (let pos = from; pos <= to; pos++) {
          const marks = state.doc.resolve(pos).marks();
          const colorMark = marks.find(m => m.type.name === 'text_color');
          if (colorMark && colorMark.attrs && (colorMark.attrs as any)['color']) {
            const color = (colorMark.attrs as any)['color'];
            if (color && !colorSet.has(color.toLowerCase())) {
              colorSet.add(color.toLowerCase());
              colors.push(color);
            }
          }
        }
      }

      // Fallback: try to get colors from DOM (for cases where ProseMirror state doesn't have it)
      const editorElement = document.querySelector('.NgxEditor__Content') as HTMLElement;
      if (!editorElement) return colors;

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return colors;

      const range = selection.getRangeAt(0);
      
      // If we have a selection, scan through all elements and text nodes in the range
      if (!empty) {
        // Method 1: Get all spans with color styles in the entire editor that intersect with selection
        const allSpans = editorElement.querySelectorAll('span[style*="color"], span[style*="Color"]');
        allSpans.forEach((span) => {
          try {
            if (range.intersectsNode(span)) {
              const element = span as HTMLElement;
              
              // Check inline style
              const styleColor = element.style.color;
              if (styleColor) {
                const hexColor = this.parseColorToHex(styleColor);
                if (hexColor && !colorSet.has(hexColor.toLowerCase())) {
                  colorSet.add(hexColor.toLowerCase());
                  colors.push(hexColor);
                }
              }
              
              // Check computed style
              const computedStyle = window.getComputedStyle(element);
              const computedColor = computedStyle.color;
              // Check if color is explicitly set via inline style (even if white or black)
              const hasInlineColor = element.style.color && element.style.color.trim().length > 0;
              
              if (computedColor) {
                // Include white/black if they're explicitly set via inline style
                if (hasInlineColor || (computedColor !== 'rgb(0, 0, 0)' && computedColor !== 'rgb(255, 255, 255)')) {
                  const hexColor = this.rgbToHex(computedColor);
                  if (hexColor && !colorSet.has(hexColor.toLowerCase())) {
                    colorSet.add(hexColor.toLowerCase());
                    colors.push(hexColor);
                  }
                }
              }
            }
          } catch (e) {
            // Ignore errors
          }
        });

        // Method 2: Walk through all nodes in the range to find colors
        const walker = document.createTreeWalker(
          range.commonAncestorContainer,
          NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
          {
            acceptNode: (node) => {
              try {
                if (range.intersectsNode(node)) {
                  return NodeFilter.FILTER_ACCEPT;
                }
              } catch (e) {
                // Ignore errors
              }
              return NodeFilter.FILTER_REJECT;
            }
          }
        );

        const processedElements = new Set<HTMLElement>();
        let node: Node | null = walker.currentNode || range.commonAncestorContainer;
        
        do {
          if (node && node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            
            // Skip if already processed
            if (processedElements.has(element)) continue;
            processedElements.add(element);
            
            // Check inline style
            const styleColor = element.style.color;
            if (styleColor) {
              const hexColor = this.parseColorToHex(styleColor);
              if (hexColor && !colorSet.has(hexColor.toLowerCase())) {
                colorSet.add(hexColor.toLowerCase());
                colors.push(hexColor);
              }
            }
            
            // Check computed style (include if inline style is set, even if white/black)
            const hasInlineColor = styleColor && styleColor.trim().length > 0;
            if (hasInlineColor || !styleColor) {
              const computedStyle = window.getComputedStyle(element);
              const computedColor = computedStyle.color;
              // Include white/black if explicitly set via inline style, otherwise exclude defaults
              if (computedColor && (hasInlineColor || (computedColor !== 'rgb(0, 0, 0)' && computedColor !== 'rgb(255, 255, 255)'))) {
                const hexColor = this.rgbToHex(computedColor);
                if (hexColor && !colorSet.has(hexColor.toLowerCase())) {
                  colorSet.add(hexColor.toLowerCase());
                  colors.push(hexColor);
                }
              }
            }
          } else if (node && node.nodeType === Node.TEXT_NODE) {
            // For text nodes, check parent element's color
            let parent = node.parentElement;
            while (parent && parent !== editorElement) {
              if (processedElements.has(parent)) break;
              processedElements.add(parent);
              
              const styleColor = parent.style.color;
              if (styleColor) {
                const hexColor = this.parseColorToHex(styleColor);
                if (hexColor && !colorSet.has(hexColor.toLowerCase())) {
                  colorSet.add(hexColor.toLowerCase());
                  colors.push(hexColor);
                  break; // Found color, no need to check further up
                }
              }
              
              const computedStyle = window.getComputedStyle(parent);
              const computedColor = computedStyle.color;
              const hasParentInlineColor = styleColor && styleColor.trim().length > 0;
              // Include white/black if explicitly set via inline style, otherwise exclude defaults
              if (computedColor && (hasParentInlineColor || (computedColor !== 'rgb(0, 0, 0)' && computedColor !== 'rgb(255, 255, 255)'))) {
                const hexColor = this.rgbToHex(computedColor);
                if (hexColor && !colorSet.has(hexColor.toLowerCase())) {
                  colorSet.add(hexColor.toLowerCase());
                  colors.push(hexColor);
                  break; // Found color, no need to check further up
                }
              }
              
              parent = parent.parentElement;
            }
          }
          
          node = walker.nextNode();
        } while (node);

        // Method 3: Also check all elements with style attribute containing color
        const allStyledElements = editorElement.querySelectorAll('[style*="color"], [style*="Color"]');
        allStyledElements.forEach((el) => {
          try {
            if (range.intersectsNode(el)) {
              const element = el as HTMLElement;
              const styleColor = element.style.color;
              if (styleColor) {
                const hexColor = this.parseColorToHex(styleColor);
                if (hexColor && !colorSet.has(hexColor.toLowerCase())) {
                  colorSet.add(hexColor.toLowerCase());
                  colors.push(hexColor);
                }
              }
            }
          } catch (e) {
            // Ignore errors
          }
        });
      } else {
        // For cursor position, find the nearest element with color
        let node: Node | null = range.startContainer;
        
        // Check the node itself if it's an element
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as HTMLElement;
          const styleColor = element.style.color;
          if (styleColor) {
            const hexColor = this.parseColorToHex(styleColor);
            if (hexColor && !colorSet.has(hexColor.toLowerCase())) {
              colorSet.add(hexColor.toLowerCase());
              colors.push(hexColor);
            }
          }
        }
        
        // Walk up the parent chain
        while (node && node !== editorElement) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            
            // Check inline style first (always include if explicitly set, even white/black)
            const styleColor = element.style.color;
            if (styleColor) {
              const hexColor = this.parseColorToHex(styleColor);
              if (hexColor && !colorSet.has(hexColor.toLowerCase())) {
                colorSet.add(hexColor.toLowerCase());
                colors.push(hexColor);
                break; // Found a color, no need to continue
              }
            }
            
            // Check computed style (include if inline style was set, even if white/black)
            const hasInlineColor = styleColor && styleColor.trim().length > 0;
            const computedStyle = window.getComputedStyle(element);
            const color = computedStyle.color;
            
            // Include white/black if explicitly set via inline style, otherwise exclude defaults
            if (color && (hasInlineColor || (color !== 'rgb(0, 0, 0)' && color !== 'rgb(255, 255, 255)'))) {
              const hexColor = this.rgbToHex(color);
              if (hexColor && !colorSet.has(hexColor.toLowerCase())) {
                colorSet.add(hexColor.toLowerCase());
                colors.push(hexColor);
                break; // Found a color, no need to continue
              }
            }
          }
          node = node.parentNode;
        }
      }
    } catch (error) {
      console.error('Error getting current text colors:', error);
    }

    return colors;
  }

  /**
   * Parse color string to hex (handles hex, rgb, rgba, named colors)
   */
  private parseColorToHex(color: string): string | null {
    if (!color) return null;
    
    color = color.trim();
    
    // If already hex
    if (color.startsWith('#')) {
      return color.length === 4 ? `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}` : color;
    }
    
    // If rgb/rgba
    if (color.startsWith('rgb')) {
      return this.rgbToHex(color);
    }
    
    // Try to convert named colors (basic set)
    const namedColors: { [key: string]: string } = {
      'black': '#000000',
      'white': '#FFFFFF',
      'red': '#FF0000',
      'green': '#008000',
      'blue': '#0000FF',
      'yellow': '#FFFF00',
      'orange': '#FFA500',
      'purple': '#800080',
      'brown': '#8B572A',
      'gray': '#808080',
      'grey': '#808080'
    };
    
    const lowerColor = color.toLowerCase();
    if (namedColors[lowerColor]) {
      return namedColors[lowerColor];
    }
    
    return null;
  }

  /**
   * Convert hex to RGB object
   */
  private hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    if (!hex) return null;
    
    hex = hex.replace('#', '');
    
    // Handle 3-digit hex
    if (hex.length === 3) {
      hex = hex.split('').map(char => char + char).join('');
    }
    
    if (hex.length !== 6) return null;
    
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
    
    return { r, g, b };
  }

  /**
   * Convert RGB/RGBA color string to hex
   */
  private rgbToHex(rgb: string): string {
    // Handle formats like rgb(0,0,0), rgba(0,0,0,1)
    const rgbMatch = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (!rgbMatch) return '#000000';

    const toHex = (n: string) => {
      const hex = parseInt(n, 10).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return (
      '#' +
      toHex(rgbMatch[1]) +
      toHex(rgbMatch[2]) +
      toHex(rgbMatch[3])
    );
  }

// Preserve trailing spaces in HTML by converting them to &nbsp;
  private preserveTrailingSpaces(html: string): string {
    if (!html) return '';
    
    const container = document.createElement('div');
    container.innerHTML = html;
    
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null
    );
    
    const textNodes: Text[] = [];
    while (walker.nextNode()) {
      textNodes.push(walker.currentNode as Text);
    }
    
    // Process text nodes in reverse to handle trailing spaces correctly
    for (let i = textNodes.length - 1; i >= 0; i--) {
      const textNode = textNodes[i];
      const value = textNode.nodeValue || '';
      
      if (value.length === 0) continue;
      
      // Check for trailing spaces
      const trailingMatch = value.match(/( +)$/);
      if (trailingMatch) {
        const spaces = trailingMatch[1];
        const beforeSpaces = value.slice(0, value.length - spaces.length);
        
        // Replace trailing spaces with non-breaking spaces
        if (beforeSpaces.length > 0) {
          textNode.nodeValue = beforeSpaces;
          // Insert non-breaking space text node after
          const nbspNode = document.createTextNode('\u00A0'.repeat(spaces.length));
          const parent = textNode.parentNode;
          if (parent) {
            parent.insertBefore(nbspNode, textNode.nextSibling);
          }
        } else {
          // Entire text node is spaces, replace with non-breaking spaces
          textNode.nodeValue = '\u00A0'.repeat(spaces.length);
        }
      }
    }
    
    return container.innerHTML;
  }

  // Function to set the cursor to the end of the content in an editable element
  setCursorToEnd(element: HTMLElement) {
    const selection = window.getSelection();
    const range = document.createRange();

    // Move the cursor to the end of the element
    range.selectNodeContents(element);
    range.collapse(false); // Collapse the range to the end (false means collapse to the end, true would collapse to the start)

    // Apply the range to the selection
    selection?.removeAllRanges();
    selection?.addRange(range);
  }


  cleanBackgroundColor() {
    const editorElement = document.querySelector('.NgxEditor__Content');
    if (!editorElement) return;

    let html = editorElement.innerHTML;
    // Remove all image tags (prevent image insertion)
    html = html.replace(/<img[^>]*>/gi, '');
    // Replace any background or background-color with transparent
    html = html.replace(/background(-color)?:\s*[^;"]+;?/gi, 'background-color: transparent;');

    // Update existing style attributes → ensure background = transparent
    // html = html.replace(/style="([^"]*)"/gi, (match, styleContent) => {
    //   // Remove previous background styles inside style=""
    //   let newStyle = styleContent.replace(/background(-color)?:\s*[^;"]+;?/gi, '');
    //   newStyle = newStyle.trim();

    //   // Add your preferred style
    // return `style="background-color: transparent; color: white; ${newStyle}"`;
    // });

    // Apply the cleaned HTML back
    editorElement.innerHTML = html;

    // Update the Angular form value
    this.form.get('editorContent')?.setValue(html, { emitEvent: true });
  }

  private countLineBreaks(html: string): number {
    // Normalize tags to <p> for counting
    const normalized = html
      .replace(/<div>/gi, '<p>')
      .replace(/<\/div>/gi, '</p>')
      .replace(/<br\s*\/?>/gi, '</p><p>');
    const paragraphs = normalized.match(/<p>.*?<\/p>/gi) || [];
    return paragraphs.length - 1; // each extra <p> is a line break
  }

  // Truncate HTML to a max text length while preserving existing formatting/tags (images removed)
  private truncateHtmlPreserveFormatting(html: string, limit: number): string {
    if (!html || limit <= 0) return '';

    const container = document.createElement('div');
    container.innerHTML = html;

    // Remove all images (prevent image insertion)
    const images = container.querySelectorAll('img');
    images.forEach(img => img.remove());

    let remaining = limit;
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
    const textNodes: { node: Text; value: string; len: number }[] = [];

    // Collect all text nodes with their values
    while (walker.nextNode()) {
      const textNode = walker.currentNode as Text;
      const value = textNode.nodeValue || '';
      const len = value.length;
      textNodes.push({ node: textNode, value, len });
    }

    // Process text nodes to truncate
    for (const { node, value, len } of textNodes) {
      if (remaining <= 0) {
        node.parentNode?.removeChild(node);
        continue;
      }

      if (len <= remaining) {
        remaining -= len;
      } else {
        // Truncate at the exact position
        node.nodeValue = value.slice(0, remaining);
        remaining = 0;
      }
    }

    // Get the truncated HTML and preserve trailing spaces
    let result = container.innerHTML;
    result = this.preserveTrailingSpaces(result);
    
    return result;
  }


  ngOnDestroy(): void {
    this.editor.destroy();
    if (this._removeTitleListener) {
      this._removeTitleListener();
      this._removeTitleListener = null;
    }
    if (this._colorPickerObserver) {
      this._colorPickerObserver.disconnect();
      this._colorPickerObserver = null;
    }
    if (this._colorMarkTimer) {
      clearTimeout(this._colorMarkTimer);
      this._colorMarkTimer = null;
    }
    if (this._popupCheckInterval) {
      clearInterval(this._popupCheckInterval);
      this._popupCheckInterval = null;
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    if (value !== undefined && value !== null) {
      // Save current cursor position before updating
      const savedRange = this.saveSelection();
      
      // Remove all image tags (prevent image insertion)
      let cleanedValue = (value || '').toString().replace(/<img[^>]*>/gi, '');
      
      // Check if this is a programmatic update (from outside) or user typing
      // Only preserve trailing spaces if the value is different from current (external update)
      const currentValue = this.form.get('editorContent')?.value || '';
      const isExternalUpdate = cleanedValue !== currentValue;
      
      let preservedValue = cleanedValue;
      if (isExternalUpdate) {
        // Preserve trailing spaces when writing value from outside (e.g., on focus, initial load)
        preservedValue = this.preserveTrailingSpaces(cleanedValue);
      }
      
      this.isApplyingProgrammaticChange = true;
      this.form.get('editorContent')?.setValue(preservedValue, { emitEvent: false });
      this.isApplyingProgrammaticChange = false;
      this.lastValidHtml = preservedValue;
      
      // Only move cursor to end if this is an external update (not during normal typing)
      setTimeout(() => {
        if (isExternalUpdate && !savedRange) {
          // Only move to end if no cursor position was saved (external update)
          const editorElement = document.querySelector('.NgxEditor__Content') as HTMLElement;
          if (editorElement) {
            this.setCursorToEnd(editorElement);
          }
        } else if (savedRange) {
          // Restore the saved cursor position
          try {
            const sel = window.getSelection();
            if (sel) {
              sel.removeAllRanges();
              sel.addRange(savedRange);
            }
          } catch (e) {
            // If restoration fails, don't move cursor
          }
        }
        
        const styles = this.getActiveMarks();  // Get active styles (bold, italic, etc.)
        this.applyToolbarActiveStyles(styles);  // Apply them to the toolbar
      }, 50);
    } else {
      setTimeout(() => {
        const styles = this.getActiveMarks();  // Get active styles (bold, italic, etc.)
        this.applyToolbarActiveStyles(styles);  // Apply them to the toolbar
      }, 50);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.form.get('editorContent')?.disable();
    } else {
      this.form.get('editorContent')?.enable();
    }
  }

  // Public method to get content
  getContent(): string {
    return this.form.get('editorContent')?.value || '';
  }

  // Public method to set content
  setContent(content: string): void {
    this.form.get('editorContent')?.setValue(content);
  }

  private applyToolbarActiveStyles(styles: any): void {
    const toolbar = document.querySelector('.NgxEditor__MenuBar');
    if (!toolbar) return;

    const btnBold = toolbar.querySelector('button[title="Bold"]');
    const btnItalic = toolbar.querySelector('button[title="Italic"]');
    const btnUnderline = toolbar.querySelector('button[title="Underline"]');
    const btnColor = toolbar.querySelector('button[title="Text Color"]');

    this.setActive(btnBold, styles.bold);
    this.setActive(btnItalic, styles.italic);
    this.setActive(btnUnderline, styles.underline);
    this.setActive(btnColor, styles.color);
  }

  private setActive(button: Element | null, isActive: boolean): void {
    if (!button) return;
    if (isActive) {
      button.classList.add('NgxEditor__MenuItem--Active');
    } else {
      button.classList.remove('NgxEditor__MenuItem--Active');
    }
  }

  /**
   * Enable toolbar buttons when editor gains focus
   */
  private enableToolbar(): void {
    const toolbar = document.querySelector('.NgxEditor__MenuBar');
    if (!toolbar) return;

    const buttons = toolbar.querySelectorAll('button');
    // buttons.forEach(button => {
    //   (button as HTMLButtonElement).disabled = false;
    //   button.classList.remove('NgxEditor__MenuItem--Disabled');
    //   button.style.opacity = '1';
    //   button.style.pointerEvents = 'auto';
    //   button.style.cursor = 'pointer';
    // });
  }

  /**
   * Disable toolbar buttons when editor loses focus
   */
  private disableToolbar(): void {
    const toolbar = document.querySelector('.NgxEditor__MenuBar');
    if (!toolbar) return;

    // const buttons = toolbar.querySelectorAll('button');
    // buttons.forEach(button => {
    //   (button as HTMLButtonElement).disabled = true;
    //   button.classList.add('NgxEditor__MenuItem--Disabled');
    //   button.style.opacity = '0.5';
    //   button.style.pointerEvents = 'none';
    //   button.style.cursor = 'not-allowed';
    // });
  }

  /**
   * Enable a specific toolbar button (used for link button)
   */
  // private enableToolbarButton(button: HTMLButtonElement | null): void {
  //   if (!button) return;
  //   button.disabled = false;
  //   button.classList.remove('NgxEditor__MenuItem--Disabled');
  //   button.style.opacity = '1';
  //   button.style.pointerEvents = 'auto';
  //   button.style.cursor = 'pointer';
  // }

  private getActiveMarks() {
    const state = this.editor.view.state;
    const sel = state.selection;
    const { from, to, empty } = sel;

    // For selections, check if there's any non-white color in the selection
    if (!empty) {
      const colors = this.getCurrentTextColors();
      // Only consider color active if there's at least one non-white color
      const hasNonWhiteColor = colors.some(color => !this.isWhiteColor(color));
      return { 
        bold: false, 
        italic: false, 
        underline: false, 
        color: hasNonWhiteColor 
      };
    }

    const marks = state.storedMarks || state.doc.resolve(from).marks();

    // Check for text_color mark and verify it's not white (default color)
    const colorMark = marks.find(m => m.type.name === 'text_color');
    let hasActiveColor = false;
    
    if (colorMark && colorMark.attrs && (colorMark.attrs as any)['color']) {
      const color = (colorMark.attrs as any)['color'];
      // Only consider color active if it's not white (white means no color applied)
      hasActiveColor = !this.isWhiteColor(color);
    }

    return {
      bold: marks.some(m => m.type.name === 'strong'),
      italic: marks.some(m => m.type.name === 'em'),
      underline: marks.some(m => m.type.name === 'u'),
      color: hasActiveColor
    };
  }

  /**
   * Check if a color is white (default/no color)
   */
  private isWhiteColor(color: string): boolean {
    if (!color) return true;
    
    const normalized = color.toLowerCase().replace('#', '').trim();
    
    // Check for white hex values
    if (normalized === 'ffffff' || normalized === 'fff') return true;
    
    // Check for named color "white"
    if (normalized === 'white') return true;
    
    // Check for RGB white values
    if (color.toLowerCase().includes('rgb')) {
      const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
      if (rgbMatch) {
        const r = parseInt(rgbMatch[1], 10);
        const g = parseInt(rgbMatch[2], 10);
        const b = parseInt(rgbMatch[3], 10);
        // Consider it white if all RGB values are 255 (or very close, like 254-255)
        if (r >= 254 && g >= 254 && b >= 254) return true;
      }
    }
    
    return false;
  }

  /**
   * Clear all formatting (bold, italic, underline, color) when editor becomes empty
   */
  private clearAllFormatting(): void {
    try {
      const state = this.editor.view.state;
      const { tr } = state;
      
      // Clear stored marks (these are applied to the next typed character)
      const transaction = tr.setStoredMarks([]);
      
      // Dispatch the transaction to clear the marks
      this.editor.view.dispatch(transaction);
      
      // Update toolbar to show default (non-active) state
      setTimeout(() => {
        const styles = { bold: false, italic: false, underline: false, color: false };
        this.applyToolbarActiveStyles(styles);
      }, 0);
    } catch (error) {
      console.error('Error clearing formatting:', error);
      // Fallback: just update toolbar UI
      setTimeout(() => {
        const styles = { bold: false, italic: false, underline: false, color: false };
        this.applyToolbarActiveStyles(styles);
      }, 0);
    }
  }
}
